/**
 * ElecBid — <RichText> : the one markdown renderer for AI replies.
 *
 * WHY THIS EXISTS
 * Three surfaces render AI replies and all three got it wrong differently:
 *   - JManWidget had a local renderMarkdown() with NO table support, so every
 *     pipe table landed as literal "| Option | Cost |" text, separator row and
 *     all ("the stupid piping").
 *   - QuoteDraftReview's RefineChat and SmartAssessment's ScopeChatCard used
 *     whiteSpace:"pre-wrap" and rendered nothing at all — **bold** showed its
 *     asterisks.
 * The models keep reaching for tables because a table IS the clearest way to
 * compare two quote options. So render them properly rather than banning them:
 * the prompts now steer toward prose for simple answers, and a real comparison
 * gets a real table.
 *
 * SAFETY: builds React elements only — never dangerouslySetInnerHTML. AI replies
 * quote client names, supplier descriptions and web-search results, so treating
 * them as HTML would be an injection path straight from a web page into the DOM.
 * Link URLs are rendered as plain text for the same reason.
 *
 * Mobile-first: tables scroll inside their own container so a wide comparison
 * never forces the whole chat bubble sideways on a phone in a van.
 */
import React, { useState } from "react";

const DEFAULT_ACCENT = "#a5ce3a";

/** Inline: `code`, **bold**, __bold__, *italic*. Returns an array of nodes. */
function inline(text, accent, keyBase) {
  const out = [];
  const re = /`([^`]+)`|\*\*([^*]+)\*\*|__([^_]+)__|\*([^*\n]+)\*/g;
  let last = 0, m, k = 0;
  const s = String(text ?? "");
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) out.push(s.slice(last, m.index));
    if (m[1] !== undefined) {
      out.push(
        <code key={`${keyBase}-c${k++}`} style={{ background: "rgba(255,255,255,0.08)", padding: "1px 5px", borderRadius: 4, fontFamily: "ui-monospace, monospace", fontSize: "0.92em" }}>{m[1]}</code>
      );
    } else if (m[2] !== undefined || m[3] !== undefined) {
      out.push(<strong key={`${keyBase}-b${k++}`} style={{ color: accent }}>{m[2] ?? m[3]}</strong>);
    } else {
      out.push(<em key={`${keyBase}-i${k++}`}>{m[4]}</em>);
    }
    last = m.index + m[0].length;
  }
  if (last < s.length) out.push(s.slice(last));
  return out.length ? out : [s];
}

// Put text on the clipboard. The modern call needs a secure page and a user
// gesture; where it is refused (an older phone browser, an embedded view) fall
// back to selecting a hidden field. Returns whether it worked.
async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch { /* fall through */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.cssText = "position:fixed;top:0;left:0;opacity:0;";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
}

/**
 * A code block at reading size with a Copy button (the SOP page). What sits in
 * these boxes is the exact text to type into a Jobber note, so one tap copies
 * it. Chat bubbles keep the plain block.
 *
 * `readOnly` is the same box with no button, for an example that is there to
 * be read. A full note with six prompts in it is one: copied in one tap and
 * left in a job, every prompt in it would run.
 */
function CopyBlock({ text, accent, readOnly = false }) {
  const [state, setState] = useState("idle"); // idle | copied | failed
  const onCopy = async () => {
    // A line break goes on the clipboard before and after the text. A prompt
    // only works at the start of a line, so a box pasted at the end of the
    // write-up, or straight after another box, was not read as a prompt.
    const ok = await copyText(`\n${text}\n`);
    setState(ok ? "copied" : "failed");
    setTimeout(() => setState("idle"), 1800);
  };
  return (
    // The button sits in its own strip above the text, not over it: laid over
    // the corner it needed room reserved beside every line, which wrapped
    // examples that fit a phone screen, and it was too small to tap.
    <div style={{ margin: "10px 0", background: "#0d1117", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 8, overflow: "hidden" }}>
      {!readOnly && (
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "6px 6px 0" }}>
          <button
            type="button"
            onClick={onCopy}
            aria-label="Copy this text"
            style={{ border: `1px solid ${state === "copied" ? accent : "rgba(255,255,255,0.18)"}`, background: state === "copied" ? accent : "#161a20", color: state === "copied" ? "#0d0f12" : "#e8eaf0", borderRadius: 8, padding: "8px 14px", minHeight: 38, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
          >
            {state === "copied" ? "Copied" : state === "failed" ? "Select it" : "Copy"}
          </button>
        </div>
      )}
      <pre style={{ padding: readOnly ? "12px 13px" : "4px 13px 12px", fontSize: 15, lineHeight: 1.5, fontFamily: "ui-monospace, monospace", overflowX: "auto", margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{text}</pre>
    </div>
  );
}

// A fence opened with three backticks and the word "read" is a box to read, not
// to copy. Only the SOP page acts on it. The opening line is never printed, in
// any mode, so the word itself does not show on screen.
const isReadFence = (line) => /^```\s*read\s*$/i.test(line.trim());

const isTableRow = (l) => /^\s*\|.*\|\s*$/.test(l);
const isTableSep = (l) => /^\s*\|?[\s:|-]*-[\s:|-]*\|?\s*$/.test(l) && l.includes("-");
const splitRow = (l) => l.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());

// The web_search tool wraps cited spans in <cite index="7-1">…</cite>. RichText
// escapes HTML (it must — see the file header), so left alone these render as
// literal "<cite index="7-1">" tags in the bubble. Strip the tags, keep the
// cited words. Runs on every render, so it also cleans the live-streaming text,
// not just the final reply.
const stripCitations = (s) => s.replace(/<\/?cite\b[^>]*>/gi, "");

// `article` (the SOP page): the same renderer at reading size — headings step
// down by level and code blocks are large enough to copy from on a phone. Chat
// bubbles leave it off and render exactly as before.
export function RichText({ text, accent = DEFAULT_ACCENT, style, article = false }) {
  const src = stripCitations(String(text ?? ""));
  if (!src.trim()) return null;
  const lines = src.split("\n");
  const els = [];
  let list = null;         // { ordered, items[] }
  let code = null;         // string[]
  let codeReadOnly = false; // the open block began with ```read

  const flushList = () => {
    if (!list || !list.items.length) { list = null; return; }
    const Tag = list.ordered ? "ol" : "ul";
    els.push(
      <Tag key={`l${els.length}`} style={{ margin: article ? "8px 0" : "4px 0", paddingLeft: article ? 22 : 20 }}>
        {list.items.map((t, i) => <li key={i} style={{ marginBottom: article ? 6 : 2 }}>{inline(t, accent, `l${els.length}-${i}`)}</li>)}
      </Tag>
    );
    list = null;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (line.trim().startsWith("```")) {
      if (code) {
        els.push(article
          ? <CopyBlock key={`p${els.length}`} text={code.join("\n")} accent={accent} readOnly={codeReadOnly} />
          : <pre key={`p${els.length}`} style={{ background: "#0d1117", padding: "8px 10px", borderRadius: 8, fontSize: 12, fontFamily: "ui-monospace, monospace", overflowX: "auto", margin: "6px 0", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{code.join("\n")}</pre>
        );
        code = null;
      } else { flushList(); code = []; codeReadOnly = isReadFence(line); }
      continue;
    }
    if (code) { code.push(line); continue; }

    // Table: a header row followed by a |---|---| separator. Without the
    // separator check a single piped sentence would become a one-row table.
    if (isTableRow(line) && i + 1 < lines.length && isTableSep(lines[i + 1])) {
      flushList();
      const head = splitRow(line);
      const rows = [];
      let j = i + 2;
      while (j < lines.length && isTableRow(lines[j])) { rows.push(splitRow(lines[j])); j++; }
      els.push(
        <div key={`t${els.length}`} style={{ overflowX: "auto", margin: "8px 0", WebkitOverflowScrolling: "touch" }}>
          <table style={{ borderCollapse: "collapse", fontSize: 12, minWidth: "100%" }}>
            <thead>
              <tr>{head.map((h, x) => (
                <th key={x} style={{ textAlign: "left", padding: "4px 8px", borderBottom: `1px solid ${accent}55`, color: accent, fontWeight: 700, whiteSpace: "nowrap" }}>{inline(h, accent, `th${x}`)}</th>
              ))}</tr>
            </thead>
            <tbody>
              {rows.map((r, y) => (
                <tr key={y}>{head.map((_, x) => (
                  <td key={x} style={{ padding: "4px 8px", borderBottom: "1px solid rgba(255,255,255,0.07)", verticalAlign: "top" }}>{inline(r[x] ?? "", accent, `td${y}-${x}`)}</td>
                ))}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      i = j - 1;
      continue;
    }

    const bullet = line.match(/^\s*[-*+]\s+(.*)$/);
    if (bullet) {
      if (!list || list.ordered) { flushList(); list = { ordered: false, items: [] }; }
      list.items.push(bullet[1]);
      continue;
    }
    const numbered = line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (numbered) {
      if (!list || !list.ordered) { flushList(); list = { ordered: true, items: [] }; }
      list.items.push(numbered[1]);
      continue;
    }
    flushList();

    if (!line.trim()) { els.push(<div key={`s${els.length}`} style={{ height: 6 }} />); continue; }

    const h = line.match(/^(#{1,4})\s+(.*)$/);
    if (h) {
      const hStyle = article
        ? { fontWeight: 700, color: h[1].length <= 2 ? "#fff" : accent, fontSize: [21, 18, 16, 15][h[1].length - 1], margin: "18px 0 6px", lineHeight: 1.25 }
        : { fontWeight: 700, color: accent, margin: "8px 0 3px" };
      els.push(<div key={`h${els.length}`} style={hStyle}>{inline(h[2], accent, `h${els.length}`)}</div>);
      continue;
    }
    // A horizontal rule reads as clutter in a chat bubble — drop it.
    if (/^\s*([-*_])\1{2,}\s*$/.test(line)) continue;

    els.push(<div key={`d${els.length}`} style={{ margin: "2px 0" }}>{inline(line, accent, `d${els.length}`)}</div>);
  }
  flushList();
  if (code) els.push(<pre key="pf" style={{ background: "#0d1117", padding: "8px 10px", borderRadius: 8, fontSize: 12, whiteSpace: "pre-wrap" }}>{code.join("\n")}</pre>);

  return <div style={{ lineHeight: 1.5, ...style }}>{els}</div>;
}

export default RichText;
